import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { ClinicalCategoryService } from '../../category/clicnical-category/clinicalCategory.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { OPDService } from '../opd.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CategoryType } from 'src/app/common/enums/category-type';
import { MatDialog } from '@angular/material/dialog';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { ClinicalitemDetailComponent } from '../../category/clinical-item/detail/clinicalitem-detail.component';
import { IClinicalCategory } from '../../category/clicnical-category/iclinical-category';
import { OPDRecord } from './iopddetails';
import { OpdCategoryPanelComponent } from './components/category-panel/category-panel.component';
import { ClinicalCategoryDetailsComponent } from '../../category/clicnical-category/detail/clinicalcategory-detail.component';

@Component({
  selector: 'app-casehistory',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    RouterModule,
    OpdCategoryPanelComponent
  ],
  templateUrl: './opddetail.component.html',
  styleUrl: './opddetail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CasehistoryComponent implements OnInit, OnChanges, OnDestroy {

  @Input() selectedtab: string;
  @Input() opdId!: number;

  listofcategory: IClinicalCategory[] = [];
  noDataAvailable: boolean = false;
  isLoadingCategories: boolean = true;
  currentCategoryIndex: number = 0;
  private emptyCategoryIds = new Set<number>();
  private contentLoadedCount = 0;

  opdID: string | null;
  checkedFromBackend: OPDRecord[] = [];
  DeleterecordID: number;

  selectedsubcategory: CommonDropDown | undefined;
  selectedcategory: CommonDropDown | undefined;

  categoryRefreshKeys: { [categoryId: number]: number } = {};

  constructor(
    private _categoryService: ClinicalCategoryService,
    private _cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private _opdService: OPDService,
    private translate: TranslateService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.opdID = this.route.snapshot.paramMap.get('opdId');

    if (this.opdID) {
      this.loadOpdData(this.opdID);
    }

    this.setDefaultPanelState();

    if (this.listofcategory) {
      this.onTabChange(0);
      this._cdr.markForCheck();
    }
  }

  /**
   * Handles updates from subcategory panels (save/delete)
   */
  onSubcategoryStateChanged(): void {
    this.refreshCheckedItems();
  }

  ngOnDestroy(): void { }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['opdId'] && changes['opdId'].currentValue) {
      this.resetComponentState();
      this.refreshCheckedItems();
      this.loadCategoriesWithItems();
    }
    if (changes['selectedtab']) {
      this.resetComponentState();
      this.loadCategoriesWithItems();
    }
  }

  /**
   * Handles category tab change
   */
  async onTabChange(index: number): Promise<void> {
    if (!this.isValidCategoryIndex(index)) {
      this.handleInvalidCategory();
      return;
    }

    const selectedCategory = this.listofcategory[index];
    this.currentCategoryIndex = index;
    this.updateSelectedCategory(selectedCategory);
    this.selectedsubcategory = undefined;
  }

  /**
   * Opens dialog to add new clinical item
   */
  onItemAdd(): void {
    const dialogRef = this.dialog.open(ClinicalitemDetailComponent, {
      autoFocus: false,
      width: '700px',
      height: 'auto',
      data: {
        fromOPDcategoryvalue: this.selectedcategory ?? null,
        fromOPDSubcategoryvalue: this.selectedsubcategory || null
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.refreshCheckedItems();
        this.reloadCategoriesSilently();
      }
    });
  }

  /**
   * Handles content loaded event from category panel
   */
  onCategoryContentLoaded(event: { categoryId: number; hasContent: boolean }): void {
    this.contentLoadedCount++;
    if (!event.hasContent) {
      this.emptyCategoryIds.add(event.categoryId);
    } else {
      this.emptyCategoryIds.delete(event.categoryId);
    }

    // Once all categories have reported, filter the list
    if (this.contentLoadedCount >= this.listofcategory.length) {
      this.listofcategory = this.listofcategory.filter(c => !this.emptyCategoryIds.has(c.id));
      this.noDataAvailable = this.listofcategory.length === 0;
      if (!this.noDataAvailable) {
        this.onTabChange(0);
      }
      this._cdr.markForCheck();
    }
  }

  /**
   * Handles selection output from category panel
   */
  onSubcategorySelected(selected?: CommonDropDown): void {
    this.selectedsubcategory = selected;
  }

  /**
   * Sets default panel state
   */
  private setDefaultPanelState(): void {
    this.selectedsubcategory = undefined;
  }

  /**
   * Resets component state when tab changes
   */
  private resetComponentState(): void {
    this.listofcategory = [];
    this.noDataAvailable = false;
    this.isLoadingCategories = true;
    this.currentCategoryIndex = 0;
    this.selectedsubcategory = undefined;
    this.categoryRefreshKeys = {};
    this.emptyCategoryIds.clear();
    this.contentLoadedCount = 0;
    this._cdr.markForCheck();
  }

  /**
   * Loads categories for the selected tab
   */
  private loadCategoriesWithItems(): void {
    this.isLoadingCategories = true;
    this.emptyCategoryIds.clear();
    this.contentLoadedCount = 0;
    this._cdr.markForCheck();
    this.refreshCheckedItems();
    this._categoryService.getCategory({
      pageNumber: 0,
      pageSize: 100,
      isDescending: false,
      categoryTypeId: this.getCategoryTypeFromLabel(this.selectedtab)
    }).subscribe((response) => {
      const categories = response?.result?.clinicalCategories || [];
      this.listofcategory = categories;
      this.noDataAvailable = categories.length === 0;
      this.isLoadingCategories = false;

      if (!this.noDataAvailable) {
        this.onTabChange(0);
      }

      this._cdr.markForCheck();
    });
  }

  /**
   * Reloads categories without showing loading indicator.
   * Preserves current tab selection when possible.
   */
  private reloadCategoriesSilently(): void {
    const currentCategoryId = this.selectedcategory?.id;
    const currentSubcategoryId = this.selectedsubcategory?.id;
    this.emptyCategoryIds.clear();
    this.contentLoadedCount = 0;

    this._categoryService.getCategory({
      pageNumber: 0,
      pageSize: 100,
      isDescending: false,
      categoryTypeId: this.getCategoryTypeFromLabel(this.selectedtab)
    }).subscribe((response) => {
      const categories = response?.result?.clinicalCategories || [];
      this.listofcategory = categories;
      this.noDataAvailable = categories.length === 0;

      // Trigger refresh on all panels via refreshKey
      categories.forEach(c => {
        this.categoryRefreshKeys = {
          ...this.categoryRefreshKeys,
          [c.id]: Date.now()
        };
      });

      // Try to restore previous tab selection
      if (!this.noDataAvailable && currentCategoryId) {
        const restoredIndex = this.listofcategory.findIndex(c => c.id === currentCategoryId);
        if (restoredIndex >= 0) {
          this.onTabChange(restoredIndex);
          if (currentSubcategoryId) {
            this.selectedsubcategory = { id: currentSubcategoryId, name: '' };
          }
        } else {
          this.onTabChange(0);
        }
      } else if (!this.noDataAvailable) {
        this.onTabChange(0);
      }

      this._cdr.markForCheck();
    });
  }

  /**
   * Triggers reload for a specific category panel
   */
  private triggerCategoryReload(categoryId?: number, subcategoryId?: number): void {
    if (!categoryId) {
      this.loadCategoriesWithItems();
      return;
    }

    this.categoryRefreshKeys = {
      ...this.categoryRefreshKeys,
      [categoryId]: Date.now()
    };
    if (subcategoryId) {
      this.selectedsubcategory = { id: subcategoryId, name: '' };
    }
  }

  /**
   * Validates category index
   */
  private isValidCategoryIndex(index: number): boolean {
    return this.listofcategory && index >= 0 && index < this.listofcategory.length;
  }

  /**
   * Handles invalid category scenario
   */
  private handleInvalidCategory(): void {
    this.noDataAvailable = this.listofcategory?.length === 0;
    this._cdr.markForCheck();
  }

  /**
   * Updates selected category
   */
  private updateSelectedCategory(category: IClinicalCategory): void {
    if (category) {
      this.selectedcategory = { id: category.id, name: category.name } as CommonDropDown;
    }
  }
  /**
   * Loads OPD data from backend
   */
  private loadOpdData(opdId: string): void {
    this._opdService.getOPDClinical(Number(opdId)).subscribe({
      next: (res) => {
        this.checkedFromBackend = res.result || [];
        this._cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error loading OPD data', err);
      }
    });
  }

  /**
   * Refreshes checked items from backend
   */
  private refreshCheckedItems(): Promise<void> {
    if (!this.opdID) {
      return Promise.resolve();
    }

    return this._opdService.getOPDClinical(Number(this.opdID)).toPromise()
      .then(res => {
        this.checkedFromBackend = res?.result || [];
        this._cdr.markForCheck();
      })
      .catch(err => {
        console.error('Failed to refresh checked items:', err);
        this.checkedFromBackend = [];
      });
  }

  /**
   * Gets translated message with replacement
   */
  private getTranslatedMessage(key: string, replaceValue: string): string {
    const message = this.translate.instant(key);
    return message.replace('{0}', replaceValue);
  }

  /**
   * Maps category label to CategoryType enum
   */
  private getCategoryTypeFromLabel(label: string): CategoryType | undefined {
    const entry = Object.entries(CategoryType).find(([key, value]) => {
      return (
        typeof value === 'string' &&
        value.toLowerCase().trim() === label.toLowerCase().trim()
      );
    });
    return entry ? (CategoryType[entry[1] as keyof typeof CategoryType] as CategoryType) : undefined;
  }

  AddClinicalCategory() {
    this.dialog.open(ClinicalCategoryDetailsComponent, {
      autoFocus: false,
      width: '700px',
      height: 'auto',
      disableClose: true,
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.loadCategoriesWithItems();
        this.setDefaultPanelState();
        this._cdr.markForCheck();
      }
    });
  }

}
